import type {
  InventoryOwnershipMode,
  InventoryState,
  InventoryUsageType,
} from '@/services/inventory'

export type InventoryLifecycleFieldKey = 'inventoryState' | 'inventoryUsageType'

export type InventoryLifecycleOperationalPatch = {
  inventoryState?: InventoryState
  inventoryStateMode?: InventoryOwnershipMode
  inventoryUsageType?: InventoryUsageType
  inventoryUsageTypeMode?: InventoryOwnershipMode
}

export type InventoryLifecycleSnapshot = {
  inventoryState?: InventoryState
  inventoryStateMode?: InventoryOwnershipMode
  inventoryUsageType?: InventoryUsageType
  inventoryUsageTypeMode?: InventoryOwnershipMode
}

export type InventoryLifecyclePendingChangeState = 'unsaved' | 'draft'

export const normalizeOwnershipMode = (
  mode: InventoryOwnershipMode | undefined,
): InventoryOwnershipMode => {
  return mode === 'MANUAL' ? 'MANUAL' : 'AUTO'
}

const changed = (before: unknown, after: unknown): boolean => {
  return JSON.stringify(before) !== JSON.stringify(after)
}

export function buildInventoryLifecycleOperationalPatch(params: {
  current: InventoryLifecycleSnapshot
  baseline: InventoryLifecycleSnapshot
  feedBacked: boolean
}): InventoryLifecycleOperationalPatch {
  const { current, baseline, feedBacked } = params
  const patch: InventoryLifecycleOperationalPatch = {}

  const currentStateMode = normalizeOwnershipMode(current.inventoryStateMode)
  const baselineStateMode = normalizeOwnershipMode(baseline.inventoryStateMode)
  const stateValueChanged = changed(baseline.inventoryState, current.inventoryState)
  const stateModeChanged = currentStateMode !== baselineStateMode

  if (feedBacked) {
    if (stateModeChanged) patch.inventoryStateMode = currentStateMode
    if (currentStateMode === 'MANUAL' && (stateModeChanged || stateValueChanged)) {
      if (current.inventoryState !== undefined) patch.inventoryState = current.inventoryState
      patch.inventoryStateMode = 'MANUAL'
    }
  } else if (stateValueChanged && current.inventoryState !== undefined) {
    patch.inventoryState = current.inventoryState
  }

  const currentUsageTypeMode = normalizeOwnershipMode(current.inventoryUsageTypeMode)
  const baselineUsageTypeMode = normalizeOwnershipMode(baseline.inventoryUsageTypeMode)
  const usageTypeValueChanged = changed(baseline.inventoryUsageType, current.inventoryUsageType)
  const usageTypeModeChanged = currentUsageTypeMode !== baselineUsageTypeMode

  if (feedBacked) {
    if (usageTypeModeChanged) patch.inventoryUsageTypeMode = currentUsageTypeMode
    if (currentUsageTypeMode === 'MANUAL' && (usageTypeModeChanged || usageTypeValueChanged)) {
      if (current.inventoryUsageType !== undefined) {
        patch.inventoryUsageType = current.inventoryUsageType
      }
      patch.inventoryUsageTypeMode = 'MANUAL'
    }
  } else if (usageTypeValueChanged && current.inventoryUsageType !== undefined) {
    patch.inventoryUsageType = current.inventoryUsageType
  }

  return patch
}

export function hasLifecyclePendingChange(params: {
  mode: InventoryOwnershipMode
  baselineMode: InventoryOwnershipMode
  valueChanged: boolean
}): boolean {
  const { mode, baselineMode, valueChanged } = params
  if (mode === 'AUTO' && baselineMode === 'MANUAL') return true
  return mode === 'MANUAL' && (baselineMode !== 'MANUAL' || valueChanged)
}

export function buildLifecyclePendingCopy(params: {
  label: string
  valueLabel: string
  mode: InventoryOwnershipMode
  baselineMode: InventoryOwnershipMode
  valueChanged: boolean
  changeState?: InventoryLifecyclePendingChangeState
}): string | null {
  const { valueLabel, mode, baselineMode, valueChanged, changeState = 'draft' } = params

  if (!hasLifecyclePendingChange({ mode, baselineMode, valueChanged })) {
    return null
  }

  const isReturnToAuto = mode === 'AUTO' && baselineMode === 'MANUAL'

  if (changeState === 'unsaved') {
    if (isReturnToAuto) {
      return 'Unsaved · Applies after save and publish.'
    }
    return `Unsaved · Applies ${valueLabel} after save and publish.`
  }

  if (isReturnToAuto) {
    return 'Draft saved · Publish to return to automatic control.'
  }
  return `Draft saved · Publish to apply ${valueLabel}.`
}
